import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Hero } from "../sections/Hero";
import { EditorialMarquee } from "../sections/EditorialMarquee";
import { Reveal, Overline } from "../components/motion";
import { FOCUS_AREAS, CREDENTIALS, IMAGES } from "../data";

const Teaser = ({ to, index, title, desc, image }) => (
  <Reveal delay={index * 0.06}>
    <Link to={to} className="group relative block bg-bg h-full overflow-hidden border border-ink/10" data-testid={`home-teaser-${index}`}>
      <div className="relative h-52 overflow-hidden">
        <img src={image} alt={title} className="img-grayscale w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-1000" />
        <div className="absolute inset-0 bg-gradient-to-t from-bg to-transparent" />
      </div>
      <div className="p-8">
        <h3 className="font-heading font-bold text-ink text-xl tracking-tight flex items-center justify-between">
          {title}
          <ArrowRight size={18} className="text-ink/30 group-hover:text-brand-green group-hover:translate-x-1 transition-all duration-300" />
        </h3>
        <p className="mt-3 font-body text-sm text-ink/55 leading-relaxed">{desc}</p>
      </div>
    </Link>
  </Reveal>
);

export default function Home() {
  return (
    <>
      <Hero />
      <EditorialMarquee />

      {/* Overview */}
      <section className="py-24 md:py-32 bg-bg" data-testid="home-overview">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <Reveal><Overline>A proven foundation · A new platform</Overline></Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-ink text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">
                  India's electric transition is becoming an <span className="font-display italic font-light text-ink/60">asset-financing opportunity.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-4">
              <Reveal delay={0.1}>
                <p className="font-body text-ink/60 leading-relaxed">Ashutosh is applying its historic strengths — capital, assets, risk and relationships — to a new and rapidly scaling asset class.</p>
              </Reveal>
            </div>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            <Teaser to="/green-finance" index={0} title="Electric Buses" desc="Vehicle acquisition and fleet expansion — anchored by the ₹190 Cr Green Mobility architecture." image={IMAGES.busEka} />
            <Teaser to="/green-finance" index={1} title="Charging Infrastructure" desc="DC fast charging, depot power and electrical infrastructure with Make-in-India partners." image={IMAGES.cubenzCharging} />
            <Teaser to="/roadmap" index={2} title="Path to ₹1,000 Cr" desc="A diversified Green Finance book targeted for March 2028, or earlier." image={IMAGES.evFleet} />
          </div>
        </div>
      </section>

      {/* Credentials strip */}
      <section className="py-20 bg-surface border-y border-ink/10" data-testid="home-credentials">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <Reveal><Overline>The numbers that define our credentials</Overline></Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
            {CREDENTIALS.slice(0, 4).map((c, i) => (
              <Reveal key={c.label} delay={i * 0.05}>
                <div className="bg-surface p-8 h-full">
                  <div className="font-heading font-extrabold text-3xl lg:text-4xl text-ink tracking-tight">{c.value}</div>
                  <div className="mt-3 font-body text-sm text-ink/55 leading-snug">{c.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/legacy" className="mt-8 inline-flex items-center gap-2 font-heading text-sm font-semibold text-brand-green hover:text-ink transition-colors" data-testid="home-legacy-link">
              Explore our 43-year track record <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA band */}
      <section className="py-24 md:py-32 bg-bg" data-testid="home-cta">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <div className="border border-ink/10 bg-surface p-10 md:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <Overline>43+ Years · ₹190 Crore · ₹1,000 Crore AUM Target</Overline>
              <h2 className="mt-5 font-heading font-extrabold tracking-tighter text-ink text-3xl md:text-5xl leading-[1]">Capital for the transition ahead.</h2>
            </div>
            <Link to="/contact" data-testid="home-cta-btn" className="shrink-0 inline-flex items-center gap-3 font-heading text-sm font-semibold tracking-wide bg-brand-green text-white px-9 py-4 hover:bg-ink hover:text-white transition-colors duration-300">
              Start the conversation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
